import type { ComponentType } from 'react';
import type { AppError, AsyncResult } from '@shared/utils';
import { err, ok } from '@shared/utils';
import type { TelehealthAdapter } from '../../src/interfaces/TelehealthAdapter';
import type { TelehealthMeetingComponentProps } from '../../src/interfaces/TelehealthMeetingComponentProps';
import type { TelehealthReservationInput } from '../../src/interfaces/TelehealthReservationInput';
import type { TelehealthReservationResult } from '../../src/interfaces/TelehealthReservationResult';
import type { TelehealthSessionCredentials } from '../../src/interfaces/TelehealthSessionCredentials';
import type { TelehealthSessionInput } from '../../src/interfaces/TelehealthSessionInput';
import { JitsiMeetingWrapper } from '../components/JitsiMeetingWrapper';
import { JITSI_CONSTANTS } from '../constants/JITSI_CONSTANTS';

const BROWSER_ONLY_MINT_ERROR: AppError = {
  errorCode: 'TELEHEALTH_BROWSER_ONLY',
  errorMessage: 'mintSessionCredentials is not available in browser; use server adapter',
};

const BROWSER_ONLY_VALIDATE_ERROR: AppError = {
  errorCode: 'TELEHEALTH_BROWSER_ONLY',
  errorMessage: 'validateRoom is not available in browser; use server adapter',
};

/**
 * @group Adapters
 *
 * @summary
 * <blockquote>Browser-side Jitsi/JaaS adapter. Returns meeting UI component via
 * getMeetingComponent; mintSessionCredentials and validateRoom return err (server-only).</blockquote>
 *
 * @example
 * ```typescript
 * import { JitsiBrowserTelehealthAdapter } from '@shared/telehealth/jitsi';
 *
 * const adapter = new JitsiBrowserTelehealthAdapter();
 * const result = await adapter.getMeetingComponent(props);
 * ```
 */
export class JitsiBrowserTelehealthAdapter implements TelehealthAdapter {
  readonly supportedRuntimes = ['browser'] as const;
  readonly providerName = JITSI_CONSTANTS.PROVIDER_NAME;

  async mintSessionCredentials(
    _input: TelehealthSessionInput
  ): AsyncResult<TelehealthSessionCredentials, AppError> {
    return err(BROWSER_ONLY_MINT_ERROR);
  }

  async validateRoom(
    _input: TelehealthReservationInput
  ): AsyncResult<TelehealthReservationResult, AppError> {
    return err(BROWSER_ONLY_VALIDATE_ERROR);
  }

  async getMeetingComponent(
    _props: TelehealthMeetingComponentProps
  ): AsyncResult<ComponentType<TelehealthMeetingComponentProps>, AppError> {
    return ok(JitsiMeetingWrapper);
  }
}
